import { useLang } from 'hooks/lang';

import { actions, isLeader, Invoice } from 'entities';

import { Column, Row, Alert, Checkbox, Modal, Button, Label } from 'components/Blocks';

import { SendIcon } from 'components/Icons';

import { useToast } from 'providers/Toast';
import { useFormControl, useUser } from 'hooks';

import { MESSAGES } from './messages';

import { downloadExcel } from './utils';
import { getFirst } from 'utils/array';
import { formatCurrency } from 'utils/text';

interface SentButtonProps {
  children?: never;
  invoices: Invoice[];
  disabled?: boolean;
  onSuccess?: () => void;
}

// MAKE IT BETTER LATER
const COUNTRIES_THAT_SEND_EMAIL = ['USA'];

export function MarkInvoiceAsSentButton({
  invoices,
  onSuccess,
  disabled,
}: SentButtonProps): JSX.Element {
  const user = useUser();

  const { t } = useLang();

  const { state, dispatch, resetDefault } = useFormControl({
    emailModalOpen: false,
    suppressIds: {} as Record<string, boolean>,
    emailCheckbox: false,
  });

  const { addToast } = useToast();

  const sendoff = actions.invoice.sendInvoicesToAccountant.use({
    params: {
      invoices: invoices.map(({ id }) => id),
      suppressIds: Object.entries(state.suppressIds)
        .filter(([, value]) => value)
        .map(([id]) => id),
    },

    onError: resetDefault,

    onSuccess: (data) => {
      downloadExcel(data);

      resetDefault();

      onSuccess?.();
    },
  });

  const markAsSent = async () => {
    const anyNotDownloaded = invoices.find(({ downloaded }) => !downloaded);

    if (anyNotDownloaded) return addToast(MESSAGES.excelNotDownloaded(t));

    if (!COUNTRIES_THAT_SEND_EMAIL.includes(getFirst(invoices)?.country)) return sendoff.onClick();

    dispatch({ emailModalOpen: true });
  };

  return (
    <>
      <Modal
        isOpen={state.emailModalOpen}
        close={resetDefault}
        title={t('suppress-automatic-email?') as string}
        width="min(90vw, 750px)"
      >
        {isLeader(user) ? (
          <>
            <Column>
              {invoices.map(({ clientName, value, currency, id }) => (
                <Row key={`confirmation-${id}`} gap="2">
                  <Checkbox
                    checked={state.suppressIds[id]}
                    onChangeDirect={(checked) =>
                      dispatch({
                        suppressIds: ({ suppressIds }) => ({
                          ...suppressIds,
                          [id]: checked,
                        }),
                      })
                    }
                  />

                  <Label>{`${clientName} - ${formatCurrency(value, currency)}`}</Label>
                </Row>
              ))}
            </Column>
          </>
        ) : (
          <>
            <Alert level="warning" title={t('us-invoice-sendoff-user-title')}>
              {t('us-invoice-sendoff-user-description')}
            </Alert>

            <Row gap="2">
              <Checkbox
                checked={state.emailCheckbox}
                onChangeDirect={(checked) => dispatch({ emailCheckbox: checked })}
              />

              <Label>{t('i-acknowledge-every-invoice-will-generate-an-email')}</Label>
            </Row>
          </>
        )}

        <Row justifyContent="flex-end">
          <Button onClick={resetDefault}>{t('cancel')}</Button>

          <Button
            disabled={disabled || sendoff.loading || isLeader(user) ? state.emailCheckbox : false}
            {...sendoff.buttonProps}
            variant="outlined"
          >
            <SendIcon />
            {t('mark-as-sent')}
          </Button>
        </Row>
      </Modal>

      <Button onClick={markAsSent} loading={sendoff.loading} variant="outlined">
        <SendIcon />
        {t('mark-as-sent')}
      </Button>
    </>
  );
}
